/**
 * IP地区检测与站点切换提示组件
 * 检测用户IP地址，根据地区提示切换站点
 */
class IpRegionBanner {
  constructor() {
    // 北美国家代码（美国和加拿大）
    this.NA_COUNTRIES = ['US', 'CA'];

    // 欧洲国家代码
    this.EU_COUNTRIES = [
      'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
      'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
      'NO', 'PL', 'PT', 'RO', 'RS', 'SK', 'SI', 'ES', 'SE', 'CH',
      'TR', 'GB', 'MK'
    ];

    // 当前站点信息
    this.currentHost = window.location.hostname;
    this.isGlobalSite = this.currentHost.includes('global.satellai.com');
    this.isMainSite = this.currentHost.includes('satellai.com') && !this.isGlobalSite;
  }

  /**
   * 初始化 Alpine 组件
   */
  init() {
    const self = this;

    return {
      // 数据状态
      visible: false,
      userCountry: '',
      userCountryName: '',
      targetSiteUrl: '',
      isAccountBased: false, // 标识提示是基于账户region(true)还是IP检测(false)

      /**
       * Alpine.js 生命周期钩子 - 组件挂载后自动调用
       */
      init() {
        this.checkAndShowBanner();
      },

      /**
       * 检查并显示横幅
       */
      checkAndShowBanner() {
        // 检查 URL 路径 - 如果以 /apps/ 开头，则不执行任何检测和弹窗
        const currentPath = window.location.pathname;
        if (currentPath.startsWith('/apps/')) {
          console.log('[IP Region Banner] URL 路径以 /apps/ 开头，跳过 IP 和 region 检测');
          this.visible = false;
          return;
        }

        // 检查localStorage - 用户是否永久关闭
        if (localStorage.getItem('ip_region_banner_dismissed')) {
          this.visible = false;
          return;
        }

        // 检查sessionStorage - 当前标签页是否关闭
        if (sessionStorage.getItem('ip_region_banner_closed')) {
          this.visible = false;
          return;
        }

        // 优先检查用户账户 region
        const accountRegionData = self.checkUserRegionMismatch();
        if (accountRegionData) {
          // 用户已登录且 region 与当前站点不匹配
          this.isAccountBased = true;
          this.userCountry = accountRegionData.region;
          this.userCountryName = self.getCountryName(accountRegionData.region);
          this.targetSiteUrl = accountRegionData.targetSiteUrl;

          // 缓存用户账户 region 到全局 store
          Alpine.store('ipDetection', {
            country: accountRegionData.region,
            isAccountBased: true,
            timestamp: Date.now()
          });

          this.visible = true;
          return;
        }

        // 如果用户已登录且有 region（即使与当前站点匹配），也不再执行 IP 检测
        // 账户 region 优先级最高
        if (window.cognitoUserInfo && window.cognitoUserInfo.getRegion()) {

          // 用户已登录，账户 region 与当前站点匹配，不显示 banner
          this.visible = false;
          // 缓存账户 region 信息（虽然不显示 banner，但记录用户的 region）
          Alpine.store('ipDetection', {
            country: window.cognitoUserInfo.getRegion(),
            isAccountBased: true,
            timestamp: Date.now()
          });

          console.log('[IP Region Banner] 用户已登录，账户 region 与当前站点匹配，不显示 banner');
          return;
        }

        // 如果用户未登录或没有 region，才执行 IP 检测
        // 后台调用API，不阻塞
        self.fetchIpInfo()
          .then(ipInfo => {
            if (ipInfo && ipInfo.suggestedSite && ipInfo.confidence) {
              this.userCountry = ipInfo.country;

              // 缓存 IP 检测结果到全局 store
              Alpine.store('ipDetection', {
                country: ipInfo.country,
                isAccountBased: false,
                timestamp: Date.now()
              });

              // 判断是否需要显示提示
              const shouldShow = self.shouldShowBanner(ipInfo.suggestedSite, ipInfo.confidence);

              if (shouldShow) {
                this.isAccountBased = false;
                this.userCountryName = self.getCountryName(ipInfo.country);
                this.targetSiteUrl = self.getTargetSiteUrl(ipInfo.suggestedSite);
                this.visible = true;
              } else {
                // 不需要显示，隐藏 banner
                this.visible = false;
              }
            } else {
              // API 失败，默认隐藏
              this.visible = false;
            }
          })
          .catch(error => {
            console.error('IP地区检测失败:', error);
            // API 失败，默认隐藏
            this.visible = false;
          });
      },

      /**
       * 关闭横幅（当前标签页有效）
       */
      closeBanner() {
        sessionStorage.setItem('ip_region_banner_closed', 'true');
        this.visible = false;
      },

      /**
       * 不再提醒（永久关闭）
       */
      dismissBanner() {
        localStorage.setItem('ip_region_banner_dismissed', 'true');
        this.visible = false;
      },

      /**
       * 立即切换站点
       */
      switchSite() {
        if (this.targetSiteUrl) {
          window.location.href = this.targetSiteUrl;
        }
      }
    };
  }

  /**
   * 检查用户账户 region 是否与当前站点匹配
   * @returns {Object|null} 如果需要提示切换，返回 {region, targetSiteUrl}，否则返回 null
   */
  checkUserRegionMismatch() {
    // 检查 cognitoUserInfo 是否可用
    if (!window.cognitoUserInfo) {
      return null;
    }

    try {
      // 获取用户的 region
      const userRegion = window.cognitoUserInfo.getRegion();
      if (!userRegion) {
        return null;
      }

      // 获取该 region 推荐的站点
      const recommendedSite = this.getRecommendedSiteByRegion(userRegion);

      // 判断是否需要提示切换
      if (recommendedSite === 'main' && this.isGlobalSite) {
        // 用户 region 属于北美，但当前在全球站
        return {
          region: userRegion,
          targetSiteUrl: this.getTargetSiteUrl('main')
        };
      } else if (recommendedSite === 'global' && this.isMainSite) {
        // 用户 region 属于欧洲或其他地区，但当前在主站
        return {
          region: userRegion,
          targetSiteUrl: this.getTargetSiteUrl('global')
        };
      }

      // 用户在正确的站点，不需要提示
      return null;
    } catch (error) {
      console.error('检查用户 region 失败:', error);
      return null;
    }
  }

  /**
   * 根据 region 代码获取推荐的站点
   * @param {String} region - 用户的 region 代码 (如 'US', 'DE', 'FR')
   * @returns {String} 推荐的站点类型 ('main' 或 'global')
   */
  getRecommendedSiteByRegion(region) {
    // 北美国家 → satellai.com (main)
    if (this.NA_COUNTRIES.includes(region)) {
      return 'main';
    }

    // 欧洲国家 → global.satellai.com (global)
    if (this.EU_COUNTRIES.includes(region)) {
      return 'global';
    }

    // 其他国家/地区 → global.satellai.com (global)
    return 'global';
  }

  /**
   * 调用API获取IP信息和推荐站点
   * @returns {Object|null} IP信息对象 {country, continent, suggestedSite, confidence, signals} 或 null
   */
  async fetchIpInfo() {
    try {
      const config = window.EnvironmentConfig;
      const apiUrl = config.getConfig().ipInfoApiUrl;

      // 获取客户端时区偏移量（单位：小时）
      const timezoneOffset = -new Date().getTimezoneOffset() / 60;

      const response = await fetch(`${apiUrl}?timezoneOffset=${timezoneOffset}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'text/plain'
        }
      });

      const result = await response.json();

      if (result.data && result.data.country) {
        return {
          country: result.data.country,
          continent: result.data.continent,
          suggestedSite: result.data.suggestedSite,
          confidence: result.data.confidence,
          signals: result.data.signals
        };
      }

      if (result.error) {
        console.error('IP Info API错误:', result.error);
      }

      return null;
    } catch (error) {
      console.error('获取IP信息失败:', error);
      return null;
    }
  }

  /**
   * 判断是否需要显示横幅
   * @param {String} suggestedSite - API 推荐的站点 ('main' 或 'global')
   * @param {Number} confidence - 置信度 (0-100)
   * @returns {Boolean} 是否需要显示横幅
   */
  shouldShowBanner(suggestedSite, confidence) {
    // 置信度阈值，低于此值不显示横幅（避免误判）
    // 设置为 90 表示只有 IP 和时区两个信号完全一致时才显示
    const CONFIDENCE_THRESHOLD = 90;

    if (confidence < CONFIDENCE_THRESHOLD) {
      return false;
    }

    // 规则1：API 推荐主站，但当前在全球站 → 提示切换到主站
    if (suggestedSite === 'main' && this.isGlobalSite) {
      return true;
    }

    // 规则2：API 推荐全球站，但当前在主站 → 提示切换到全球站
    if (suggestedSite === 'global' && this.isMainSite) {
      return true;
    }

    return false;
  }

    /**
   * 获取目标站点URL
   * @param {String} suggestedSite - 推荐的站点 ('main' 或 'global')
   * @returns {String} 目标站点URL
   */
  getTargetSiteUrl(suggestedSite) {
    const currentPath = window.location.search;

    if (suggestedSite === 'main') {
      // 切换到主站
      return 'https://satellai.com' + currentPath;
    } else {
      // 切换到global站点
      return 'https://global.satellai.com' + currentPath;
    }
  }

  /**
   * 获取国家名称（英文）
   * @param {String} countryCode - 国家代码
   * @returns {String} 国家名称
   */
  getCountryName(countryCode) {
    const countryNames = {
      'US': 'United States',
      'CA': 'Canada',
      'GB': 'United Kingdom',
      'DE': 'Germany',
      'FR': 'France',
      'IT': 'Italy',
      'ES': 'Spain',
      'NL': 'Netherlands',
      'JP': 'Japan',
      'AU': 'Australia',
      'CN': 'China',
      'KR': 'South Korea',
      'IN': 'India',
      'BR': 'Brazil',
      'MX': 'Mexico',
      'AR': 'Argentina',
      'CL': 'Chile',
      'CO': 'Colombia',
      'PE': 'Peru',
      'VE': 'Venezuela',
      'SG': 'Singapore',
      'MY': 'Malaysia',
      'TH': 'Thailand',
      'ID': 'Indonesia',
      'PH': 'Philippines',
      'VN': 'Vietnam',
      'NZ': 'New Zealand',
      'ZA': 'South Africa',
      'NG': 'Nigeria',
      'EG': 'Egypt',
      'KE': 'Kenya',
      'SA': 'Saudi Arabia',
      'AE': 'United Arab Emirates',
      'IL': 'Israel',
      'TR': 'Turkey',
      'RU': 'Russia',
      'UA': 'Ukraine',
      'PL': 'Poland',
      'RO': 'Romania',
      'CZ': 'Czech Republic',
      'HU': 'Hungary',
      'HK': 'Hong Kong',
      'GR': 'Greece',
      'PT': 'Portugal',
      'SE': 'Sweden',
      'NO': 'Norway',
      'DK': 'Denmark',
      'FI': 'Finland',
      'IE': 'Ireland',
      'BE': 'Belgium',
      'AT': 'Austria',
      'CH': 'Switzerland'
    };

    return countryNames[countryCode] || countryCode;
  }
}

// 注册Alpine组件
document.addEventListener('alpine:init', () => {
  // 初始化 IP 检测数据存储（用于缓存 IP/Region 信息）
  Alpine.store('ipDetection', {
    country: null,          // 国家代码（如 'US', 'DE'）
    isAccountBased: false,  // 是否基于账户 region
    timestamp: null         // 缓存时间戳
  });

  Alpine.data('ipRegionBanner', () => new IpRegionBanner().init());
});
